import { cn } from "@/lib/utils";

/**
 * Commerce's table look, lifted from DataTable's `cardTheme` variant and the
 * TableShell that wraps it. Applied from the shell with descendant variants so
 * every column inherits it without editing each cell.
 *
 * It lives here, not in MetaAdsPage, because the campaign and ad set detail
 * pages print the same tables and have to stay in step with the list.
 */
export const TABLE_SHELL_CLS =
  "isolate overflow-hidden rounded-2xl border border-[#EAECF0] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

export const TABLE_CARD_THEME_CLS = cn(
  "[&_thead_tr]:border-0 [&_thead_tr]:border-b [&_thead_tr]:border-[#EAECF0] [&_thead_tr]:bg-[#FAFAFB] [&_thead_tr:hover]:bg-[#FAFAFB]",
  "[&_th]:h-auto [&_th]:border-0 [&_th]:bg-[#FAFAFB] [&_th]:px-3.5 [&_th]:py-3 [&_th]:text-[10.5px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-[0.06em] [&_th]:text-[#98A2B3] [&_th]:whitespace-nowrap",
  "[&_tbody_tr]:border-b [&_tbody_tr]:border-[#F2F4F7] [&_tbody_tr:last-child]:border-b-0",
  // Size and spacing are forced onto every cell; the body colour is only
  // inherited, so a cell that deliberately dims itself still can.
  "text-[#101828]",
  "[&_td]:border-0 [&_td]:px-3.5 [&_td]:py-3.5 [&_td]:align-middle [&_td]:text-[13px]",
);

// `group` is load-bearing with the sticky first/actions columns: those cells
// paint their own opaque background, so they can only follow the row's hover
// through `group-hover:` (see CARD_THEME_STICKY_* in DataTable).
export const TABLE_ROW_CLS = "group cursor-pointer transition hover:bg-[#F9FAFB]";

/**
 * The first column of the Campaigns, Ad Sets and Ads tables.
 *
 * 🔴 ONE WIDTH FOR ALL THREE. Each tab had grown its own — Campaigns and Ad
 * Sets asked for `min-w-[200px]`, Ads for `w-[280px] min-w-[240px]` — and
 * because the Ads cell also carries a 36px thumbnail, the three name columns
 * landed at three different widths. Switching tabs shifted every metric
 * column sideways. A fixed `w-` makes the three agree whatever a row holds;
 * the names truncate inside it.
 */
export const TABLE_NAME_COL_CLS = "w-[260px] min-w-[200px]";

/**
 * The content box inside that column.
 *
 * 🔴 THE HEADER'S WIDTH ALONE DOES NOT HOLD. `truncate` sets
 * `white-space: nowrap`, and a nowrap run's MIN-CONTENT is the whole string —
 * so an auto-layout table widens the column to fit the longest name whatever
 * `w-` says, and `min-w-0` does not reduce a block's min-content. Ad names
 * ("Purchase · Broad Advantage+ — Ad 1") are longer than campaign names, which
 * is why the Ads column stayed wider. Pinning the inner box is what actually
 * caps it: 260px column − 2 × 14px cell padding.
 */
export const TABLE_NAME_CELL_CLS = "w-[232px] max-w-[232px] min-w-0";
